import { Card, CardHeader as MuiCardHeader, Paper, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

// Enhanced Styled Card component for professional dashboard styling
export const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(0),
  borderRadius: theme.spacing(2),
  boxShadow: `0 4px 20px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)'}`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.12)'}`,
    transform: 'translateY(-2px)',
  },
}));

// Enhanced CardHeader with gradient background
export const CardHeader = styled(MuiCardHeader)(({ theme }) => ({
  flexShrink: 0,
  height: 56,
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.common.white,
  padding: theme.spacing(0, 3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
  },
  '& .MuiCardHeader-title': {
    fontSize: '1.1rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
  },
}));

// Professional row item for task lists
export const TaskRowItem = styled(Paper)(({ theme, status, isOverdue }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
  boxShadow: `0 2px 8px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)'}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '4px',
    background: status === 'COMPLETED' ? theme.palette.success.main :
               status === 'REJECTED' ? theme.palette.error.main :
               status === 'IN_PROGRESS' ? theme.palette.warning.main :
               status === 'FORWARDED' ? theme.palette.info.main :
               isOverdue ? theme.palette.error.main :
               theme.palette.primary.main,
    borderRadius: '0 2px 2px 0',
  },
  '&:hover': {
    boxShadow: `0 4px 16px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)'}`,
    transform: 'translateX(4px)',
    borderColor: theme.palette.primary.main,
  },
}));

// Status indicator component
export const StatusIndicator = styled(Box)(({ theme, status }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor: status === 'COMPLETED' ? theme.palette.success.light :
                 status === 'REJECTED' ? theme.palette.error.light :
                 status === 'IN_PROGRESS' ? theme.palette.warning.light :
                 status === 'FORWARDED' ? theme.palette.info.light :
                 theme.palette.primary.light,
  color: status === 'COMPLETED' ? theme.palette.success.dark :
         status === 'REJECTED' ? theme.palette.error.dark :
         status === 'IN_PROGRESS' ? theme.palette.warning.dark :
         status === 'FORWARDED' ? theme.palette.info.dark :
         theme.palette.primary.dark,
}));

// Priority indicator component
export const PriorityIndicator = styled(Box)(({ theme, priority }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.spacing(0.5),
  fontSize: '0.7rem',
  fontWeight: 600,
  backgroundColor: priority === 'HIGH' || priority === 'عاجل' ? theme.palette.error.light :
                 priority === 'MEDIUM' || priority === 'اطمینانیه' ? theme.palette.warning.light :
                 theme.palette.success.light,
  color: priority === 'HIGH' || priority === 'عاجل' ? theme.palette.error.dark :
         priority === 'MEDIUM' || priority === 'اطمینانیه' ? theme.palette.warning.dark :
         theme.palette.success.dark,
}));

// Mapping of statuses to MUI palette color keys
export const getStatusColor = (status) => {
  const colors = {
    ASSIGNED: 'primary',
    ACCEPTED: 'info',
    IN_PROGRESS: 'warning',
    COMPLETED: 'success',
    REJECTED: 'error',
    FORWARDED: 'secondary',
    DRAFT: 'text.secondary',
    SUBMITTED: 'info',
    IN_REVIEW: 'warning',
    APPROVED: 'success',
    CANCELLED: 'error',
    active: 'success',
    inactive: 'error',
  };
  return colors[status] || 'default';
};

// Centralised colour palette for charts
export const chartColors = {
  LOW: '#4caf50',
  MEDIUM: '#ffeb3b',
  HIGH: '#ff9800',
  URGENT: '#f44336',
  LETTER: '#2196f3',
  MEMO: '#673ab7',
  REPORT: '#009688',
  PROPOSAL: '#ff5722',
  REQUEST: '#3f51b5',
  APPROVAL: '#8bc34a',
  OTHER: '#9e9e9e',
  ASSIGNED: '#42a5f5',
  ACCEPTED: '#9ccc65',
  IN_PROGRESS: '#ffb74d',
  COMPLETED: '#66bb6a',
  REJECTED: '#ef5350',
};
